import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { CloseIcon } from '@/components/ui/Icons';
import '@/components/ui/Toast/Toast.css';

export type ToastType = 'success' | 'error';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  /** Фаза плавного исчезновения перед удалением из DOM */
  exiting?: boolean;
}

interface ToastContextValue {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  removeToast: (id: string) => void;
  /** Завершить удаление после анимации исчезновения (вызывает только ToastViewport) */
  completeDismissal: (id: string) => void;
  clearAutoDismiss: (id: string) => void;
  scheduleAutoDismiss: (id: string) => void;
  toasts: ToastItem[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 5000;
/** Длительность анимации исчезновения (см. Toast.css @keyframes toast-out) */
const EXIT_ANIMATION_FALLBACK_MS = 400;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const exitFallbackTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const completeDismissal = useCallback((id: string) => {
    const fb = exitFallbackTimersRef.current.get(id);
    if (fb) {
      clearTimeout(fb);
      exitFallbackTimersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const removeToast = useCallback(
    (id: string) => {
      const autoTimer = timersRef.current.get(id);
      if (autoTimer) {
        clearTimeout(autoTimer);
        timersRef.current.delete(id);
      }

      let shouldScheduleFallback = false;
      setToasts((prev) => {
        const t = prev.find((x) => x.id === id);
        if (!t || t.exiting) return prev;
        shouldScheduleFallback = true;
        return prev.map((x) => (x.id === id ? { ...x, exiting: true } : x));
      });

      if (shouldScheduleFallback) {
        const fb = setTimeout(() => {
          exitFallbackTimersRef.current.delete(id);
          completeDismissal(id);
        }, EXIT_ANIMATION_FALLBACK_MS);
        exitFallbackTimersRef.current.set(id, fb);
      }
    },
    [completeDismissal]
  );

  const clearAutoDismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const scheduleAutoDismiss = useCallback((id: string) => {
    clearAutoDismiss(id);
    const timer = setTimeout(() => {
      removeToast(id);
      timersRef.current.delete(id);
    }, AUTO_DISMISS_MS);
    timersRef.current.set(id, timer);
  }, [clearAutoDismiss, removeToast]);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setToasts((prev) => [...prev, { id, type, message }]);
    scheduleAutoDismiss(id);
  }, [scheduleAutoDismiss]);

  const showSuccess = useCallback((message: string) => addToast('success', message), [addToast]);
  const showError = useCallback((message: string) => addToast('error', message), [addToast]);

  const value: ToastContextValue = {
    showSuccess,
    showError,
    removeToast,
    completeDismissal,
    clearAutoDismiss,
    scheduleAutoDismiss,
    toasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport />
    </ToastContext.Provider>
  );
}

/** Рендерится только внутри Provider — useContext, не useToast (избегаем циклических импортов с UI-модулем). */
function ToastViewport() {
  const ctx = useContext(ToastContext);
  if (!ctx) return null;
  const { toasts, removeToast, completeDismissal, clearAutoDismiss, scheduleAutoDismiss } = ctx;
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" role="region" aria-label="Уведомления">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast_${t.type}${t.exiting ? ' toast_exiting' : ''}`}
          role="alert"
          onMouseEnter={() => clearAutoDismiss(t.id)}
          onMouseLeave={() => scheduleAutoDismiss(t.id)}
          onAnimationEnd={(e) => {
            if (e.currentTarget !== e.target) return;
            if (!t.exiting) return;
            if (e.animationName !== 'toast-out') return;
            completeDismissal(t.id);
          }}
        >
          <span className="toast__message">{t.message}</span>
          <button
            type="button"
            className="toast__close"
            onClick={() => removeToast(t.id)}
            aria-label="Закрыть"
          >
            <CloseIcon size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
